<script context="module" lang="ts">
    import { writable } from "svelte/store"

    export const sideMenuOpen = writable(false)
    function close() {
        sideMenuOpen.set(false)
    }
</script>

<script lang="ts">
    import Button from "@components/button/Button.svelte"
    import { openAuth } from "@components/forms/Authenticate.svelte"
    import HelpIcon from "@components/help/HelpIcon.svelte"
    import { openHelp } from "@components/help/Help.module"
    import Icon from "@components/material/Icon.svelte"
    import { openInstall } from "@components/onboarding/Install.svelte"
    import { goto } from "$app/navigation"
    import { donationLink } from "@utils/links"
    import { installedPWA, isNative } from "@utils/underhood/browser"
    import { signOut, user, userStatus, verifyEmail } from "@utils/underhood/firebase"
    import { fly, fade } from "svelte/transition"
    import { openFontSizeEditor } from "@components/bottomPopup/FontSize.module"
    import SideMenuHighlightList from "./SideMenuHighlightList.svelte"

    function clickOutside(e: PointerEvent) {
        if ($sideMenuOpen) {
            e.preventDefault()
            e.stopPropagation()
            $sideMenuOpen = false
        }
    }

    function openAccount(register: boolean) {
        openAuth(register)
        close()
    }

    function openAppearance() {
        close()
        openFontSizeEditor()
    }

    function openGuide() {
        close()
        openHelp()
    }

    const duration = 400
    let emailSent = false
</script>

{#if $sideMenuOpen}
    <aside
        class="study-menu"
        role="navigation"
        aria-label="More menu"
        aria-hidden={!$sideMenuOpen}
        transition:fly|global={{ x: 400, duration, opacity: 1 }}
    >
        <div class="study-menu-header">
            <div>
                <span>Menu</span>
                <h2>Study resources</h2>
            </div>
            <button type="button" aria-label="Close menu" class="menu-close" on:click={close}>
                <Icon icon="close" />
            </button>
        </div>

        <div class="study-menu-scroll">
            <section class="menu-section" aria-labelledby="menu-study">
                <h3 id="menu-study">Study</h3>
                <div class="menu-stack">
                    <div class="menu-row">
                        <a class="menu-item" on:click={close} href="/account/highlights">
                            <span class="menu-icon"><Icon icon="highlighter" /></span>
                            <span>Highlights & highlighters</span>
                        </a>
                        <div class="menu-help">
                            <HelpIcon path="managing-highlighters" on:opened={close} />
                        </div>
                    </div>
                    <div class="menu-sublist" aria-label="Highlighters">
                        <SideMenuHighlightList />
                    </div>
                    <div class="menu-row">
                        <a class="menu-item" on:click={close} href="/account/highlights?notes=true">
                            <span class="menu-icon"><Icon icon="notes" /></span>
                            <span>Notes</span>
                        </a>
                        <div class="menu-help">
                            <HelpIcon path="taking-notes" on:opened={close} />
                        </div>
                    </div>
                    <div class="menu-row">
                        <a class="menu-item" on:click={close} href="/book">
                            <span class="menu-icon"><Icon icon="bookmarks" /></span>
                            <span>Tab list</span>
                        </a>
                        <div class="menu-help">
                            <HelpIcon path="using-tabs" on:opened={close} />
                        </div>
                    </div>
                    <div class="menu-row">
                        <button class="menu-item" type="button" on:click={openAppearance}>
                            <span class="menu-icon"><Icon icon="visual" /></span>
                            <span>Appearance</span>
                        </button>
                        <div class="menu-help">
                            <HelpIcon path="appearance" on:opened={close} />
                        </div>
                    </div>
                </div>
            </section>

            <section class="menu-section" aria-labelledby="menu-resources">
                <h3 id="menu-resources">Resources</h3>
                <div class="menu-stack">
                    <button class="menu-item" type="button" on:click={openGuide}>
                        <span class="menu-icon"><Icon icon="help-bw" /></span>
                        <span>User guide & tips</span>
                    </button>
                    <a class="menu-item" on:click={close} href="/about">
                        <span class="menu-icon"><Icon icon="information" /></span>
                        <span>About</span>
                    </a>
                    <a
                        class="menu-item"
                        rel="noreferrer"
                        target="_blank"
                        href={donationLink}
                        on:click={close}
                    >
                        <span class="menu-icon"><Icon icon="donate" /></span>
                        <span>Donate</span>
                    </a>
                    <a class="menu-item" on:click={close} href="/key">
                        <span class="menu-icon"><Icon icon="key" /></span>
                        <span>Reference key</span>
                    </a>
                    {#if !(installedPWA || isNative)}
                        <button
                            class="menu-item"
                            type="button"
                            on:click={() => {
                                close()
                                openInstall("invite")
                            }}
                        >
                            <span class="menu-icon"><Icon icon="install" /></span>
                            <span>Install</span>
                        </button>
                    {/if}
                </div>
            </section>
        </div>

        <section class="account-panel" aria-labelledby="menu-account">
            <div class="account-header">
                <div>
                    <span>Account</span>
                    <h3 id="menu-account">Sync & settings</h3>
                </div>
                {#if $userStatus != "out"}
                    <button
                        type="button"
                        class="account-settings-button"
                        on:click={() => {
                            goto("/account/settings")
                            close()
                        }}
                        aria-label="Account settings"
                    >
                        <Icon icon="settings" />
                    </button>
                {/if}
            </div>

            {#if $userStatus == "out"}
                <p>
                    You are currently signed out. Create an account to sync tabs, highlights, notes,
                    and practice across devices.
                </p>
                <div class="account-actions">
                    <Button color="purple" icon="create-account-sm" go={() => openAccount(true)}
                        >Create account</Button
                    >
                    <Button color="white" icon="key" go={() => openAccount(false)}>Sign in</Button>
                </div>
            {:else}
                {#if $userStatus == "verified" || $userStatus == "unverified"}
                    <p>
                        Signed in as <strong>{$user?.email}</strong>
                    </p>
                {/if}
                {#if $userStatus == "unverified"}
                    <p class="warning">
                        You have not verified your email address yet. Sync will begin after
                        verification.
                    </p>
                {/if}
                <div class="account-actions">
                    {#if $userStatus == "unverified"}
                        <Button
                            disabled={emailSent}
                            color="white"
                            go={() =>
                                verifyEmail()
                                    .then(() => (emailSent = true))
                                    .catch(() => (emailSent = true))}>Resend email</Button
                        >
                    {/if}
                    <Button
                        color="white"
                        go={async () => {
                            await signOut()
                            close()
                        }}>Sign out</Button
                    >
                </div>
            {/if}
        </section>
    </aside>
    <div
        class="side-menu-backdrop"
        transition:fade|global={{ duration }}
        on:pointerup|capture={clickOutside}
    />
{/if}

<style>
    .study-menu {
        --side-menu-surface: var(--color-grey-0);
        --side-menu-border: var(--color-purple-100);
        --side-menu-muted-surface: var(--color-purple-50);
        --side-menu-text: var(--color-grey-900);
        --side-menu-muted-text: var(--color-grey-600);
        --side-menu-label: var(--color-purple-700);
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        z-index: 1001;
        display: grid;
        grid-template-rows: auto minmax(0, 1fr) auto;
        width: min(100vw, 22rem);
        max-width: 100vw;
        height: 100dvh;
        min-height: 0;
        overflow: hidden;
        border-left: 1px solid var(--side-menu-border);
        background: var(--side-menu-surface);
        color: var(--side-menu-text);
        box-shadow: none;
    }

    .study-menu-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 1rem;
        border-bottom: 1px solid var(--side-menu-border);
        padding: calc(1rem + var(--safe-area-inset-top)) 1rem 0.95rem;
    }

    .study-menu-header > div {
        display: grid;
        gap: 0.35rem;
        min-width: 0;
    }

    .menu-close {
        appearance: none;
        display: grid;
        place-items: center;
        flex: 0 0 auto;
        width: 2.5rem;
        height: 2.5rem;
        border: 1px solid var(--side-menu-border);
        border-radius: 0.65rem;
        background: var(--side-menu-surface);
        color: var(--side-menu-label);
        cursor: pointer;
        opacity: 1;
    }

    .menu-close :global(svg) {
        width: 1rem;
        height: 1rem;
    }

    .study-menu-scroll {
        display: grid;
        align-content: start;
        gap: 1.25rem;
        min-height: 0;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        background: var(--side-menu-surface);
        padding: 1rem;
    }

    .study-menu-header span,
    .account-header span {
        color: var(--side-menu-label);
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.025em;
        line-height: 1;
        text-transform: uppercase;
    }

    .study-menu-header h2,
    .menu-section h3,
    .account-header h3,
    .account-panel p {
        margin: 0;
    }

    .study-menu-header h2,
    .menu-section h3,
    .account-header h3 {
        color: var(--side-menu-text);
        font-family: Lora, ui-serif, Georgia, Cambria, "Times New Roman", Times, serif;
        font-weight: 500;
        letter-spacing: 0;
        line-height: 1.2;
    }

    .study-menu-header h2 {
        font-size: 1.45rem;
    }

    .menu-section {
        display: grid;
        gap: 0.65rem;
    }

    .menu-section h3,
    .account-header h3 {
        font-size: 1.15rem;
    }

    .menu-stack {
        display: grid;
        gap: 0.35rem;
    }

    .menu-row {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 2.1rem;
        align-items: center;
        gap: 0.4rem;
    }

    .menu-item {
        appearance: none;
        display: grid;
        grid-template-columns: auto minmax(0, 1fr);
        align-items: center;
        gap: 0.75rem;
        min-height: 2.75rem;
        width: 100%;
        border: 1px solid transparent;
        border-radius: 0.75rem;
        background: transparent;
        color: var(--side-menu-muted-text);
        cursor: pointer;
        font: inherit;
        font-size: 0.95rem;
        font-weight: 650;
        letter-spacing: 0;
        line-height: 1.25;
        padding: 0.65rem 0.75rem;
        text-align: left;
        text-decoration: none;
        transition:
            border-color 0.16s ease,
            background-color 0.16s ease,
            color 0.16s ease;
    }

    button.menu-item {
        font-family:
            Inter,
            ui-sans-serif,
            system-ui,
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            sans-serif;
    }

    .menu-item:hover,
    .menu-item:focus-visible {
        border-color: var(--side-menu-border);
        background: var(--side-menu-muted-surface);
        color: var(--side-menu-label);
    }

    .menu-item:focus-visible,
    .menu-close:focus-visible,
    .account-settings-button:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 2px;
    }

    .menu-icon {
        display: grid;
        place-items: center;
        width: 1.5rem;
        height: 1.5rem;
        color: var(--side-menu-label);
    }

    .menu-icon :global(svg) {
        width: 1.15rem;
        height: 1.15rem;
    }

    .menu-help {
        display: grid;
        place-items: center;
        width: 2.1rem;
        height: 2.1rem;
        border: 1px solid transparent;
        border-radius: 0.65rem;
        color: var(--side-menu-label);
    }

    .menu-help :global(.help-path) {
        width: 1rem;
        height: 1rem;
        margin-left: 0;
        opacity: 0.72;
    }

    .menu-help :global(svg) {
        width: 1rem;
        height: 1rem;
    }

    .menu-sublist {
        max-height: 5.25rem;
        overflow: auto;
        border-left: 1px solid var(--side-menu-border);
        margin: 0.15rem 0 0.45rem 1.55rem;
        padding-left: 0.7rem;
    }

    .account-panel {
        display: grid;
        gap: 1rem;
        border-top: 1px solid var(--side-menu-border);
        background: var(--side-menu-surface);
        padding: 1rem 1rem calc(1rem + var(--safe-area-inset-bottom));
    }

    .account-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
    }

    .account-settings-button {
        appearance: none;
        display: grid;
        place-items: center;
        flex: 0 0 auto;
        width: 2.4rem;
        height: 2.4rem;
        border: 1px solid var(--side-menu-border);
        border-radius: 0.65rem;
        background: var(--side-menu-surface);
        color: var(--side-menu-label);
        cursor: pointer;
    }

    .account-settings-button :global(svg) {
        width: 1rem;
        height: 1rem;
    }

    .account-panel p {
        color: var(--side-menu-muted-text);
        font-size: 0.95rem;
        line-height: 1.55;
    }

    .account-panel strong {
        color: var(--side-menu-label);
        font-weight: 700;
    }

    .warning {
        border: 1px solid var(--color-gold-300);
        border-radius: 0.75rem;
        background: var(--color-gold-50);
        padding: 0.75rem;
    }

    .account-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
    }

    .side-menu-backdrop {
        position: fixed;
        inset: 0;
        z-index: 1000;
        background: rgb(0 0 0 / 0.45);
    }

    @media (max-height: 700px) {
        .menu-sublist {
            display: none;
        }
    }

    @media only screen and (max-width: 24rem) {
        .account-actions,
        .account-actions :global(.app-button) {
            width: 100%;
        }
    }

    :global(.darkmode) .study-menu {
        --side-menu-surface: var(--color-grey-900);
        --side-menu-border: var(--color-grey-700);
        --side-menu-muted-surface: var(--color-grey-800);
        --side-menu-text: var(--color-grey-50);
        --side-menu-muted-text: var(--color-grey-200);
        --side-menu-label: var(--color-purple-200);
    }

    :global(.darkmode) .warning {
        border-color: var(--color-gold-700);
        background: var(--color-grey-800);
    }
</style>
